// fetchUtils.js
import { collection, doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase'; // Adjust the import according to your Firebase setup

/**
 * Fetch all documents from a Firestore collection in real-time.
 * @param {string} collectionName - Name of the Firestore collection.
 * @param {function} callback - Callback function to handle the fetched data.
 * @returns {function} - Unsubscribe function to stop the real-time listener.
 */
export const fetchCollection = (collectionName, callback) => {
  const collectionRef = collection(db, collectionName);

  // Set up a real-time listener using onSnapshot
  const unsubscribe = onSnapshot(collectionRef, (querySnapshot) => {
    const data = [];
    querySnapshot.forEach((doc) => {
      data.push({ id: doc.id, ...doc.data() }); // Include document ID in the data
    });
    callback(data); // Pass the fetched data to the callback
  });

  return unsubscribe; // Return the unsubscribe function to clean up the listener
};

/**
 * Fetch a single document from a Firestore collection by its ID.
 * @param {string} collectionName - Name of the Firestore collection.
 * @param {string} docId - ID of the document to fetch.
 * @param {function} callback - Callback function to handle the fetched document.
 * @returns {function} - Unsubscribe function to stop the real-time listener.
 */
export const fetchDocumentById = (collectionName, docId, callback) => {
  const docRef = doc(db, collectionName, docId);

  // Set up a real-time listener using onSnapshot
  const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      callback({ id: docSnapshot.id, ...docSnapshot.data() }); // Include document ID in the data
    } else {
      callback(null); // Document does not exist
    }
  });

  return unsubscribe; // Return the unsubscribe function to clean up the listener
};

/**
 * Fetch a single document from a Firestore collection (non-real-time).
 * @param {string} collectionName - Name of the Firestore collection.
 * @param {string} docId - ID of the document to fetch.
 * @returns {Promise} - Resolves with the document data or null if not found.
 */
export const getDocumentById = async (collectionName, docId) => {
  const docRef = doc(db, collectionName, docId);
  const docSnapshot = await getDoc(docRef);

  if (docSnapshot.exists()) {
    return { id: docSnapshot.id, ...docSnapshot.data() }; // Include document ID in the data
  } else {
    return null; // Document does not exist
  }
};